import { Field, Item, Text, getFieldValue, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import { useState } from 'react';

import Button, { ButtonVariant } from 'components/helpers/Button/Button';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import { type ComponentProps } from 'lib/component-props';

import styles from './AccordionItem.module.scss';
import { RecordOfFields } from 'models/fields';

const cx = classNames.bind(styles);

type AccordionItemFields = {
  body: Field<string>;
  headingLevel: Item;
  headingLevelStyle: Item | null;
  title: Field<string>;
};

export type AccordionItemProps = ComponentProps & {
  fields: AccordionItemFields;
  id: string;
};

const AccordionItem = (props: AccordionItemProps): React.JSX.Element => {
  const { fields, id } = props;
  const { title, body } = fields;
  const { sitecoreContext } = useSitecoreContext();
  const { pageEditing } = sitecoreContext;

  const defaultOpen = pageEditing ?? false;
  const [open, setOpen] = useState(defaultOpen);

  const buttonId = `accordion-button-${id}`;
  const contentId = `accordion-content-${id}`;

  const headingLevelItemField = fields.headingLevel?.fields as RecordOfFields;
  const headingLevelValue = getFieldValue<string>(headingLevelItemField, 'Value') || 'h3';

  const headingLevelStyleItemField = fields.headingLevelStyle?.fields as RecordOfFields;
  const headingLevelStyleValue = getFieldValue<string>(headingLevelStyleItemField, 'Value') || 'font-style-xl';

  const onClick: React.MouseEventHandler = () => {
    setOpen(!open);
  };

  return (
    <div className={cx('container')}>
      <Button
        aria-controls={contentId}
        aria-expanded={open}
        className={cx('button')}
        icon="carat"
        id={buttonId}
        onClick={onClick}
        type="button"
        variant={ButtonVariant.Ghost}
      >
        <Text
          className={cx('accordion-item-title', { title: true, [headingLevelStyleValue]: true })}
          field={title}
          tag={headingLevelValue}
        />
      </Button>
      <div aria-labelledby={buttonId} className={cx('content')} hidden={!open} id={contentId} role="region">
        <RichTextWrapper field={body} />
      </div>
    </div>
  );
};

export default AccordionItem;
